// backend.ts is the indirection layer between the public namespace modules
// (auth.ts, data.ts, userdata.ts, capture.ts, records.ts) and the backend
// implementation. The default backend is the remote transport (fetch →
// /_backlit/...). backlit.offline.enable() installs a decorator over it via
// setBackend() (offline-backend.ts), and disable() restores the remote one.
//
// Namespace modules and the JSON helpers call getBackend() on EVERY call
// (never caching the result), so a swap takes effect on the next call
// everywhere at once. setBackend is internal — never re-exported from
// index.ts.
//
// Note on the dependency graph: this file imports the implementation at
// the top level. transport.ts in turn imports the `Backend` and
// `BlobResult` types from here — that's a type-only edge, erased by
// the compiler, so there is no runtime cycle.

import { remoteBackend } from "./transport.js";

// Permission is the access level of the signed-in user (CLAUDE.md §8), a
// lowercase enum (the wire contract). null means "no signed-in user" (an
// anonymous or signed-out visitor) or "unknown" (a session minted before
// permissions shipped). A "viewer" user is
// blocked from shared-data writes (data.put / data.delete); userdata.* and
// capture.* are unaffected. The type is GENERATED from
// internal/firestore.Permissions so it can't drift from the server enum
// (cross-boundary-audit Slice 5) — edit there + run `task permissions:sync`.
// Imported for local use here and re-exported so existing
// `import { Permission } from "./backend.js"` call sites are unchanged.
import type { Permission } from "./permissions.gen.js";
export type { Permission };

// BacklitUser is the resolved identity returned by auth.me(): the five
// signed-in user fields the data-api surfaces at GET /_backlit/auth/me.
// It is an all-string type — absent uid/name/mobile decode to "" and an
// absent/unrecognized permission to "" (not null), so callers never have
// to null-check individual fields. auth.me() itself returns BacklitUser |
// null: null means no signed-in user (an anonymous or signed-out visitor),
// the whole object means there is one.
export type BacklitUser = {
  uid: string;
  name: string;
  email: string;
  mobile: string;
  permission: Permission | "";
};

// BlobResult is what data.get / userdata.get resolve to. value is the
// uncompressed bytes (the browser auto-decodes Content-Encoding: gzip
// inside fetch()); contentType is the stored content-type; crc32c is
// the base64-encoded big-endian uint32 Castagnoli checksum of the
// uncompressed body — same format as the storage.change event and the
// PUT response. Apps comparing local state against server state can
// hash their own bytes with `formatCRC32C(crc32cBytes(...))` from
// crc32c.ts and compare.
//
// Offline tags (backlit.offline): each is set ONLY when true, never as false
// or undefined (exactOptionalPropertyTypes). fromCache = answered from the
// on-device mirror because the network was unavailable; pending = a write
// still queued on this device. PutResult, the matched CAS arms and ListEntry
// rows carry pending; ListPage carries fromCache.
export type BlobResult = { value: Blob; contentType: string; crc32c: string; fromCache?: true; pending?: true };

// PutResult is what data.put / userdata.put / capture.update
// resolve to. crc32c carries the uncompressed-body integrity hash the
// server recorded, useful for asserting "what I wrote is what landed"
// without an extra GET.
export type PutResult = { crc32c: string; pending?: true };

// StoredValue is the current value of a key, returned as the witness on
// a failed compare-and-swap (data.putIfMatch / userdata.putIfMatch). Same
// shape as BlobResult — the value the caller would have read with get().
export type StoredValue = BlobResult;

// PutIfMatchResult is the outcome of a compare-and-swap write. A CRC
// mismatch (you lost the race) is a NORMAL result, not an error:
//   - { matched: true,  crc32c } — the write landed; crc32c is the
//     uncompressed-body hash of the value now stored.
//   - { matched: false, witness } — the stored value's CRC didn't match
//     the expected CRC; witness is the current value (no write happened).
// Callers narrow on `.matched`. Real failures (not_found, forbidden,
// quota_exceeded, …) still throw a BacklitError.
export type PutIfMatchResult =
  | { matched: true; crc32c: string; pending?: true }
  | { matched: false; witness: StoredValue };

// CaptureCreateResult is the PutResult shape plus the handle the
// object was stored under. The handle is "{prefix}-{guid}" — the caller's
// create() prefix prepended to a GUID the server minted or, under the
// offline capture opt-in (offline.enable({stores}) with "capture"), a
// random v4 UUID minted on the device. pending is set only when that opt-in
// queued the create on this device (the handle is final either way).
export type CaptureCreateResult = { handle: string; crc32c: string; pending?: true };

// ListEntry is one row in the data/userdata list() response. key is the
// SDK-visible path; crc32c is the uncompressed-body hash for that entry;
// contentType is the stored content-type and size is the uncompressed
// (logical) byte size, both surfaced by the data-api list endpoints
// (tolerate-missing → "" / 0 in the transport decoder).
export type ListEntry = { key: string; crc32c: string; contentType: string; size: number; pending?: true };

// ListOptions is the optional second argument to every list() surface
// (data/userdata/records/capture). It carries the cursor pagination controls
// (DATA-1): cursor is the opaque token a prior page returned as `next` ("" /
// omitted ⇒ first page); limit is the requested page size, clamped server-side
// to [1, 1000] (omitted ⇒ the server default of 500). An over-range limit is
// clamped, never rejected. Callers paginate by re-calling list() with the
// previous page's `next` until it is "".
export type ListOptions = { cursor?: string; limit?: number };

// BatchEntry is one row in the data/userdata getBatch() response — a value-
// bearing superset of ListEntry (same key/crc32c/contentType/size metadata),
// discriminated on `omitted`:
//   - included:      omitted is absent/false and `value` is the decoded Blob
//                    (a "" wire value is a real zero-byte Blob, NOT omission).
//   - metadata-only: omitted is true, `value` is absent, and `reason` (when a
//                    string) says why — the server omits an entry larger than
//                    the whole per-response budget (wire `reason: "too_large"`;
//                    open vocabulary, tolerate any string).
// Callers narrow on `.omitted` like PutIfMatchResult's `.matched` — the omitted
// arm is a NORMAL outcome, not an error. The SDK's transport decoder degrades a
// row with malformed base64 into the omitted arm too (no invented reason).
export type BatchEntry = ListEntry & (
  | { readonly omitted?: false; readonly value: Blob }
  | { readonly omitted: true; readonly value?: never; readonly reason?: string }
);

// RecordBatchEntry is the records getBatch() row: BatchEntry plus the record's
// owner uid (tolerate-missing → "", the FE8 policy) — the value-bearing analogue
// of RecordListEntry.
export type RecordBatchEntry = BatchEntry & { readonly owner: string };

// GetBatchOptions is the optional second argument to every getBatch() /
// getBatchJSON() surface. Structurally identical to ListOptions ({cursor?,
// limit?}) but a DEDICATED type: the getBatch limit is clamped server-side to
// [1, 500] (default 100), stricter than list's [1, 1000]/500, and a page may
// end early at the server's per-response value budget with `next` set — so its
// doc surface (sdk.d.ts) differs. cursor is a prior page's `next` ("" / omitted
// ⇒ first page).
export type GetBatchOptions = { cursor?: string; limit?: number };

// ListPage is what every list() surface resolves to: one bounded page of
// entries plus the opaque `next` cursor for the following page ("" when the
// listing is exhausted). E is the per-entry shape (ListEntry, CaptureListEntry,
// or RecordListEntry) — unchanged from the pre-pagination flat-array return; it
// is just wrapped here so a glow with very many keys returns a bounded page.
// complete is present only on a page answered from the on-device copy
// (offline-backend.ts): whether the listing so far is the whole prefix.
export type ListPage<E> = { items: E[]; next: string; fromCache?: true; complete?: boolean };

// CaptureListEntry is one row in the capture.list() response.
// handle is the full "{prefix}-{guid}" handle; crc32c is the
// uncompressed-body hash; contentType / size mirror ListEntry. (Separate
// from ListEntry because capture is keyed by opaque handle, not by an
// app-chosen key.)
export type CaptureListEntry = { handle: string; crc32c: string; contentType: string; size: number };

// Records-surface result types (window.backlit.records). They are
// structural intersections of the data/userdata base types plus an `owner`
// uid (CLAUDE.md §8 D7), so they remain assignable to the base types and the
// data/userdata shapes are untouched.
//
//   - RecordResult       — records.get / the CAS 412 witness: a BlobResult
//                          plus the record's owner uid.
//   - RecordListEntry    — one records.list() row: a ListEntry plus owner.
//   - RecordPutIfMatchResult — the records compare-and-swap outcome. matched
//                          carries the committed owner (the 200's
//                          X-Backlit-Record-Owner header) so updateJSON can
//                          return it; the witness carries it on a mismatch.
export type RecordResult = BlobResult & { owner: string };
export type RecordListEntry = ListEntry & { owner: string };
export type RecordPutIfMatchResult =
  | { matched: true; crc32c: string; owner: string; pending?: true }
  | { matched: false; witness: RecordResult };

// StorageChangeEvent is the payload delivered to handlers registered
// via backlit.on("storage.change", …). Emitted on every successful
// data/userdata/records set/delete; capture events are deliberately
// excluded (the store is write-only opaque telemetry — leaking handle
// creation to peers would defeat its purpose).
//
//   - store:     "data" for shared, "userdata" for the per-user silo,
//                "records" for the public-read/owner-write store.
//   - operation: "set" for PUT/POST, "delete" for DELETE.
//   - key:       The same key the writer passed to put / delete.
//   - crc32c:    Base64-encoded big-endian uint32 Castagnoli checksum of
//                the stored bytes — same encoding GCS uses in its
//                X-Goog-Hash header. Empty string on delete events.
//   - owner:     Present only on records events — the record's owner uid
//                (the creator, or the new owner on a reassign, which emits
//                operation:"set"). Absent (undefined) for data/userdata.
//   - pending:   Present (true) only on a LOCAL event (backlit.offline): the
//                write was queued on this device and hasn't reached the
//                server yet. Such an event never comes from the socket.
//
// userdata events are scoped server-side to the writing user's verified
// email; a tab signed in as user B never sees user A's userdata.* keys.
// data AND records events are unscoped — every connected session of the glow
// receives them (records reads are public, so its owner uid is too).
export interface StorageChangeEvent {
  readonly store: "data" | "userdata" | "records";
  readonly operation: "set" | "delete";
  readonly key: string;
  readonly crc32c: string;
  readonly owner?: string;
  readonly pending?: true;
}

// UIConfig is the bootstrap the SDK FAB fetches once on load (GET /_backlit/ui).
// It is decoded tolerantly (missing fields → safe defaults) like authMe, and
// never leaks platform internals.
//   - user:            the signed-in identity, or null (anonymous visitor).
//   - authMode:        "Public" | "Private" | "Accounts" (or "" if unknown).
//   - loginAvailable:  whether to offer a "Sign in" button (Accounts glows).
//   - canHideBranding: whether the owner's plan permits hiding the FAB (gates
//                      the runtime hideUI()).
//   - brandingHidden:  whether this glow is configured to hide the FAB (the
//                      per-glow paid toggle ∧ the live capability) — when true
//                      the FAB is not injected at all.
//   - marketingUrl:    the "Get your own glow" link target.
//   - title:           the glow's owner-set display Title, or "" when unset; the
//                      FAB renders it as the panel heading when non-empty.
//   - pushEnabled:     whether push is enabled for this glow (the version's
//                      notify.json opted in). false → push.subscribe throws
//                      unsupported.
//   - vapidPublicKey:  the base64url VAPID application-server key push.subscribe
//                      passes to the Push API. "" when push is disabled.
//   - pushReregisterUrl: the SW's re-subscribe endpoint the SDK stashes for the
//                      pushsubscriptionchange handler ("" → the canonical
//                      default is used).
export interface UIConfig {
  user: BacklitUser | null;
  authMode: string;
  loginAvailable: boolean;
  canHideBranding: boolean;
  brandingHidden: boolean;
  marketingUrl: string;
  title: string;
  pushEnabled: boolean;
  vapidPublicKey: string;
  pushReregisterUrl: string;
}

// PushSubscribePayload is the /_backlit/push/subscribe request body — the
// browser PushSubscription's endpoint + its p256dh/auth keys.
export interface PushSubscribePayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

// SubscribeResult is the /_backlit/push/subscribe response: the server-side
// subscription id and the opaque reregisterToken the SW stashes to authorize a
// later pushsubscriptionchange re-register (empty "" on an idempotent
// re-subscribe of an already-known endpoint).
export interface SubscribeResult {
  endpointId: string;
  reregisterToken: string;
}

// PushUnsubscribePayload is the /_backlit/push/unsubscribe request body.
export interface PushUnsubscribePayload {
  endpoint: string;
}

export type StorageChangeHandler = (event: StorageChangeEvent) => void;

// Unsubscribe is returned by on() and the backend's onStorageChange.
// Idempotent: calling it twice is a no-op.
export type Unsubscribe = () => void;

export interface Backend {
  authMe(): Promise<BacklitUser | null>;
  // authUsers returns one cursor-paginated page of the glow's user roster
  // (admin-only on the server) as a ListPage<BacklitUser> — the same
  // { items, next } shape the data/userdata/records/capture list() surfaces
  // resolve to, reusing ListOptions/ListPage. Unlike authMe, the namespace
  // does NOT swallow its errors — a forbidden / unauthenticated /
  // invalid_cursor / network failure propagates as a BacklitError to the
  // auth.users() caller.
  authUsers(opts?: ListOptions): Promise<ListPage<BacklitUser>>;
  // uiConfig fetches the FAB bootstrap (GET /_backlit/ui). Decoded tolerantly;
  // the FAB module swallows any failure (no FAB), so this may reject.
  uiConfig(): Promise<UIConfig>;
  dataGet(key: string): Promise<BlobResult>;
  dataPut(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  dataPutIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<PutIfMatchResult>;
  dataDelete(key: string): Promise<void>;
  dataList(prefix: string, opts?: ListOptions): Promise<ListPage<ListEntry>>;
  // dataGetBatch / userdataGetBatch / recordsGetBatch are the value-bearing
  // siblings of the *List methods: the same list-shaped input, the same
  // { items, next } page, but each entry carries its value (a Blob) alongside
  // the list metadata — or arrives metadata-only with omitted:true when the
  // server dropped its value at the per-response budget.
  dataGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>>;
  userdataGet(key: string): Promise<BlobResult>;
  userdataPut(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  userdataPutIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<PutIfMatchResult>;
  userdataDelete(key: string): Promise<void>;
  userdataList(prefix: string, opts?: ListOptions): Promise<ListPage<ListEntry>>;
  userdataGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>>;
  captureCreate(prefix: string, body: BodyInit, contentType: string): Promise<CaptureCreateResult>;
  captureUpdate(handle: string, body: BodyInit, contentType: string): Promise<PutResult>;
  // captureGet / captureList / captureDelete are admin-only on
  // remote glows (the server returns unauthenticated / forbidden for
  // non-admins).
  captureGet(handle: string): Promise<BlobResult>;
  captureList(prefix: string, opts?: ListOptions): Promise<ListPage<CaptureListEntry>>;
  captureDelete(handle: string): Promise<void>;
  // Records surface (window.backlit.records): public-read, owner-write.
  // recordsGet / recordsList carry the owner uid; recordsPut returns just the
  // crc (the writer is the owner); recordsPutIfMatch surfaces the owner on
  // both the matched commit and the witness; recordsReassign is admin-only.
  recordsGet(key: string): Promise<RecordResult>;
  recordsPut(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  recordsPutIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<RecordPutIfMatchResult>;
  recordsDelete(key: string): Promise<void>;
  recordsList(prefix: string, opts?: ListOptions): Promise<ListPage<RecordListEntry>>;
  recordsGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<RecordBatchEntry>>;
  recordsReassign(key: string, newOwnerUid: string): Promise<void>;
  // onStorageChange registers a handler. The handler is called from a
  // WebSocket message dispatch. Returns an unsubscribe function. Same
  // handler may be registered multiple times — each registration yields
  // its own unsubscribe.
  onStorageChange(handler: StorageChangeHandler): Unsubscribe;
  // Push surface (window.backlit.push) — the /_backlit/push/* endpoints. All
  // require a signed-in user (401 unauthenticated for an anonymous / signed-out
  // visitor). The SDK's push namespace layers the browser Push API + the
  // capability probe on top of these transport calls. (There is no server
  // "state" endpoint — push.state() reads the browser's own PushManager +
  // stashed endpoint id, and the OS Notification.permission, client-side.)
  pushSubscribe(payload: PushSubscribePayload): Promise<SubscribeResult>;
  pushUnsubscribe(payload: PushUnsubscribePayload): Promise<void>;
  pushTest(): Promise<void>;
}

let active: Backend = remoteBackend;

export function getBackend(): Backend {
  return active;
}

// setBackend installs b as the active backend; null restores the remote
// transport. Internal (offline.enable()/disable() and tests).
export function setBackend(b: Backend | null): void {
  active = b ?? remoteBackend;
}
